import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

/** Latest-year percentile row from `livability_index_precalculated` (Climate Equity Index source). */
export type LivabilityPercentileSnapshot = {
  year: number;
  ldv_emissions_percentile: number | null;
  hdv_emissions_percentile: number | null;
  elec_total_emissions_percentile: number | null;
  ng_total_emissions_percentile: number | null;
  waste_emissions_percentile: number | null;
  offroad_emissions_percentile: number | null;
  diesel_pm_percentile: number | null;
  pm2_5_percentile: number | null;
  ozone_percentile: number | null;
  tox_release_percentile: number | null;
  haz_waste_percentile: number | null;
  asthma_percentile: number | null;
  cardiovascular_disease_percentile: number | null;
  low_birth_weight_percentile: number | null;
  traffic_percentile: number | null;
  commute_time_percentile: number | null;
  transportation_cost_burden_percentile: number | null;
  housing_burden_renter_percentile: number | null;
  housing_burden_owner_percentile: number | null;
  housing_burden_percentile: number | null;
  percent_fossil_heating_percentile: number | null;
  income_equity_percentile: number | null;
  education_equity_percentile: number | null;
  wage_equity_percentile: number | null;
  median_household_income_percentile: number | null;
  employment_rate_percentile: number | null;
  job_wage_percentile: number | null;
  educational_attainment_percentile: number | null;
  health_insurance_percentile: number | null;
  ev_per_1000_people_percentile: number | null;
  ev_charging_percentile: number | null;
  public_transit_percentile: number | null;
  solar_pv_percentile: number | null;
  heat_pump_percentile: number | null;
  benefit_community_resources_percentile: number | null;
};

const SNAPSHOT_SELECT =
  'year,ldv_emissions_percentile,hdv_emissions_percentile,elec_total_emissions_percentile,ng_total_emissions_percentile,waste_emissions_percentile,offroad_emissions_percentile,diesel_pm_percentile,pm2_5_percentile,ozone_percentile,tox_release_percentile,haz_waste_percentile,asthma_percentile,cardiovascular_disease_percentile,low_birth_weight_percentile,traffic_percentile,commute_time_percentile,transportation_cost_burden_percentile,housing_burden_renter_percentile,housing_burden_owner_percentile,housing_burden_percentile,percent_fossil_heating_percentile,income_equity_percentile,education_equity_percentile,wage_equity_percentile,median_household_income_percentile,employment_rate_percentile,job_wage_percentile,educational_attainment_percentile,health_insurance_percentile,ev_per_1000_people_percentile,ev_charging_percentile,public_transit_percentile,solar_pv_percentile,heat_pump_percentile,benefit_community_resources_percentile';

const num = (raw: unknown): number | null => {
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return Number(raw);
};

function mapRow(row: Record<string, unknown>): LivabilityPercentileSnapshot {
  return {
    year: Number(row.year),
    ldv_emissions_percentile: num(row.ldv_emissions_percentile),
    hdv_emissions_percentile: num(row.hdv_emissions_percentile),
    elec_total_emissions_percentile: num(row.elec_total_emissions_percentile),
    ng_total_emissions_percentile: num(row.ng_total_emissions_percentile),
    waste_emissions_percentile: num(row.waste_emissions_percentile),
    offroad_emissions_percentile: num(row.offroad_emissions_percentile),
    diesel_pm_percentile: num(row.diesel_pm_percentile),
    pm2_5_percentile: num(row.pm2_5_percentile),
    ozone_percentile: num(row.ozone_percentile),
    tox_release_percentile: num(row.tox_release_percentile),
    haz_waste_percentile: num(row.haz_waste_percentile),
    asthma_percentile: num(row.asthma_percentile),
    cardiovascular_disease_percentile: num(row.cardiovascular_disease_percentile),
    low_birth_weight_percentile: num(row.low_birth_weight_percentile),
    traffic_percentile: num(row.traffic_percentile),
    commute_time_percentile: num(row.commute_time_percentile),
    transportation_cost_burden_percentile: num(row.transportation_cost_burden_percentile),
    housing_burden_renter_percentile: num(row.housing_burden_renter_percentile),
    housing_burden_owner_percentile: num(row.housing_burden_owner_percentile),
    housing_burden_percentile: num(row.housing_burden_percentile),
    percent_fossil_heating_percentile: num(row.percent_fossil_heating_percentile),
    income_equity_percentile: num(row.income_equity_percentile),
    education_equity_percentile: num(row.education_equity_percentile),
    wage_equity_percentile: num(row.wage_equity_percentile),
    median_household_income_percentile: num(row.median_household_income_percentile),
    employment_rate_percentile: num(row.employment_rate_percentile),
    job_wage_percentile: num(row.job_wage_percentile),
    educational_attainment_percentile: num(row.educational_attainment_percentile),
    health_insurance_percentile: num(row.health_insurance_percentile),
    ev_per_1000_people_percentile: num(row.ev_per_1000_people_percentile),
    ev_charging_percentile: num(row.ev_charging_percentile),
    public_transit_percentile: num(row.public_transit_percentile),
    solar_pv_percentile: num(row.solar_pv_percentile),
    heat_pump_percentile: num(row.heat_pump_percentile),
    benefit_community_resources_percentile: num(row.benefit_community_resources_percentile),
  };
}

/**
 * Fetch the latest livability index row for a location (by `location_name`, not geofips).
 * Tries City/Place/County geography types and years 2023 → 2020.
 */
export async function fetchLatestLivabilityPercentileSnapshot(
  locationName: string,
  preferredYear = 2023
): Promise<LivabilityPercentileSnapshot | null> {
  const loc = (locationName || '').trim();
  if (!loc) return null;

  const locType = supabaseLocationService.getLocationType(loc);
  const geoTypes: string[] =
    locType === 'county'
      ? ['County']
      : locType === 'state'
        ? ['State']
        : ['City', 'Place'];

  const years = [preferredYear, preferredYear - 1, preferredYear - 2, preferredYear - 3].filter(
    (y) => y >= 2005
  );

  for (const year of years) {
    for (const gt of geoTypes) {
      const { data, error } = await supabaseClient
        .from('livability_index_precalculated')
        .select(SNAPSHOT_SELECT)
        .eq('location_name', loc)
        .eq('geography_type', gt)
        .eq('year', year)
        .limit(1);
      if (!error && data?.[0]) return mapRow(data[0] as Record<string, unknown>);
    }
  }

  for (const gt of geoTypes) {
    const { data, error } = await supabaseClient
      .from('livability_index_precalculated')
      .select(SNAPSHOT_SELECT)
      .eq('location_name', loc)
      .eq('geography_type', gt)
      .not('total_score_percentile', 'is', null)
      .order('year', { ascending: false })
      .limit(1);
    if (!error && data?.[0]) return mapRow(data[0] as Record<string, unknown>);
  }

  return null;
}
